import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type RefObject,
} from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import {
  calendarDayOf,
  dayOf,
  daysInMonth,
  formatDate,
  isoOf,
  parseGermanDay,
  todayIso,
  type CalendarDay,
} from '@brickup/shared/utils'
import { Button } from './Button'
import { cn } from './cn'
import { useDisabled, useQuietField } from './control-scope'
import { controlShell, EMPTY_MARK, Field } from './Field'
import { Popover } from './Popover'
import { FOCUS_RING } from './recipes'
import { TOUCH_QUERY, useMediaQuery } from './use-media-query'

const WORDS = CONTROLS.dateInput

const germanOf = (iso: string | null | undefined) => formatDate(iso, '')

/** The day `delta` days after `from`, across months and years. */
const shift = (from: CalendarDay, delta: number) =>
  calendarDayOf(new Date(from.year, from.month - 1, from.day + delta))

/** The same day `delta` months on, held to the last day of a shorter month. */
const shiftMonth = (from: CalendarDay, delta: number): CalendarDay => {
  const { year, month } = calendarDayOf(
    new Date(from.year, from.month - 1 + delta, 1),
  )
  return { year, month, day: Math.min(from.day, daysInMonth(year, month)) }
}

/** Monday is 0, as the grid starts the week. */
const weekdayOf = ({ year, month, day }: CalendarDay) =>
  (new Date(year, month - 1, day).getDay() + 6) % 7

const same = (a: CalendarDay | null, b: CalendarDay | null) =>
  !!a && !!b && a.year === b.year && a.month === b.month && a.day === b.day

/**
 * The text as it is typed, with the dots set: `0103` reads `01.03.`, and a
 * comma, slash or dash from a number pad becomes a dot. Deleting is left
 * alone, so a backspace over a dot is not put straight back, and so is an
 * edit inside the text or over a selection: the mask would shift what
 * follows it.
 */
function withDots(previous: string, next: string): string {
  if (!next.startsWith(previous) || /^\d{4}-/.test(next)) return next
  return (
    next
      .replace(/[,/\-\s]/g, '.')
      .replace(/[^\d.]/g, '')
      // The dot typed after one the mask already set is the same dot.
      .replace(/\.{2,}/g, '.')
      .replace(/^(\d{2})(?=\d|$)/, '$1.')
      .replace(/^(\d{1,2}\.\d{2})(?=\d|$)/, '$1.')
      .slice(0, 10)
  )
}

/** Typed far enough that it ought to be a day. A two-digit year is not: it
 *  is also the first half of a four-digit one still being typed. */
const isWhole = (text: string) =>
  /^(\d{1,2}\.\d{1,2}\.\d{4}|\d{4}-\d{2}-\d{2})$/.test(text)

const monthTitle = new Intl.DateTimeFormat('de-DE', {
  month: 'long',
  year: 'numeric',
})
const longDay = new Intl.DateTimeFormat('de-DE', { dateStyle: 'full' })
const monthShort = new Intl.DateTimeFormat('de-DE', { month: 'short' })
const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
  monthShort.format(new Date(2000, i, 1)),
)

// ── The field ───────────────────────────────────────────────────────────────

type DateInputProps = {
  /** An ISO day, `YYYY-MM-DD`; empty is `null`. */
  value: string | null | undefined
  /** Called with an ISO day once what is typed is a whole real day, and with
   *  `null` when the field is cleared. */
  onValueChange?: (value: string | null) => void
  label?: string
  helper?: string
  error?: string
  className?: string
  /** Datasheet row: no box until hover or focus. */
  quiet?: boolean
  disabled?: boolean
  autoFocus?: boolean
}

/** The props once the scopes around the field have had their say. */
type ResolvedProps = DateInputProps & { quiet: boolean; disabled: boolean }

/**
 * A day, always written `TT.MM.JJJJ`.
 *
 * On a phone or a tablet the tap opens the operating system's own picker —
 * the wheel on iOS, the calendar on Android — which is what a thumb expects
 * and what the keyboard there cannot beat. On a desktop the day is typed,
 * the dots set themselves, and a calendar opens under the field to pick
 * from or to check what was typed against.
 *
 * Either way the field writes German, whatever language the browser speaks,
 * and holds the ISO day the API speaks.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput(props, ref) {
    const touch = useMediaQuery(TOUCH_QUERY)
    const resolved: ResolvedProps = {
      ...props,
      quiet: useQuietField(props.quiet),
      disabled: useDisabled(props.disabled),
    }
    return touch ? (
      <NativeDateInput ref={ref} {...resolved} />
    ) : (
      <TypedDateInput ref={ref} {...resolved} />
    )
  },
)

/** The operating system's picker under a field that writes German. */
const NativeDateInput = forwardRef<HTMLInputElement, ResolvedProps>(
  function NativeDateInput(
    {
      value,
      onValueChange,
      label,
      helper,
      error,
      className,
      quiet,
      disabled,
      autoFocus,
    },
    ref,
  ) {
    const shown = germanOf(value)

    return (
      <Field label={label} helper={helper} error={error} className={className}>
        {field => (
          <div className="relative">
            {/* The real control lies over the box, invisible: the tap is
                the platform's, the text under it is ours. */}
            <input
              ref={ref}
              {...field}
              type="date"
              value={value ?? ''}
              disabled={disabled}
              autoFocus={autoFocus}
              onChange={event => onValueChange?.(event.target.value || null)}
              className="peer absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-default"
            />
            <div
              aria-hidden
              className={controlShell(
                quiet,
                cn(
                  'flex items-center justify-between gap-2 tabular-nums',
                  'peer-focus:border-primary/60 peer-focus:ring-2 peer-focus:ring-primary/15',
                  quiet &&
                    'peer-hover:border-border peer-hover:bg-card peer-focus:bg-card',
                  'peer-disabled:bg-line-soft peer-disabled:text-faint',
                  error && 'border-destructive',
                ),
              )}
            >
              <span className={shown ? undefined : 'text-faint'}>
                {shown || (quiet ? EMPTY_MARK : WORDS.placeholder)}
              </span>
              <CalendarDays size={14} className="shrink-0 text-faint" />
            </div>
          </div>
        )}
      </Field>
    )
  },
)

/** Typed with the dots set, and the app's calendar under it. */
const TypedDateInput = forwardRef<HTMLInputElement, ResolvedProps>(
  function TypedDateInput(
    {
      value,
      onValueChange,
      label,
      helper,
      error,
      className,
      quiet,
      disabled,
      autoFocus,
    },
    ref,
  ) {
    const [text, setText] = useState(() => germanOf(value))
    const [shownValue, setShownValue] = useState(value)
    const [open, setOpen] = useState(false)
    const [left, setLeft] = useState(false)
    const input = useRef<HTMLInputElement>(null)
    const calendar = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => input.current!)

    const parsed = parseGermanDay(text)

    // A value set from outside replaces the text. One the field sent itself
    // leaves the operator's way of writing it alone, and text that is no
    // day stays to be corrected.
    const sent = useRef<string | null | undefined>(value)
    if (value !== shownValue) {
      setShownValue(value)
      if ((value || null) !== (sent.current || null)) {
        sent.current = value
        setText(germanOf(value))
        setLeft(false)
      }
    }

    const commit = (iso: string | null) => {
      sent.current = iso
      if (iso !== (value || null)) onValueChange?.(iso)
    }

    const type = (raw: string) => {
      const next = withDots(text, raw)
      setText(next)
      if (next.trim() === '') return commit(null)
      // A day goes out the moment it is whole, so a form submitted with
      // Enter straight after sees it.
      const day = isWhole(next) ? parseGermanDay(next) : null
      if (day) commit(day)
    }

    /** What is typed, settled: a shorthand written out; text that is no day
     *  stays as typed, marked, and leaves the field without a value, so a
     *  form cannot save a day the field does not show. */
    const settle = () => {
      setLeft(true)
      commit(parsed)
      if (parsed) setText(germanOf(parsed))
    }

    const pick = (iso: string | null) => {
      setText(germanOf(iso))
      commit(iso)
      setOpen(false)
    }

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!open) return setOpen(true)
        calendar.current
          ?.querySelector<HTMLElement>('[role="grid"] [tabindex="0"]')
          ?.focus()
      } else if (event.key === 'Enter') {
        // A shorthand is written out first and the next Enter submits; text
        // that is no day yet submits nothing, or the form would save the
        // last day while the field shows another.
        if (text.trim() !== '' && (!parsed || germanOf(parsed) !== text)) {
          event.preventDefault()
          if (parsed) settle()
        }
        setOpen(false)
      } else if (event.key === 'Tab') {
        setOpen(false)
      }
    }

    const invalid =
      text.trim() !== '' && parsed == null && (left || isWhole(text))

    // The form the field sits in refuses to submit while it holds text that
    // is no day: the value is empty then, and an optional date would
    // otherwise be saved as cleared. Set before the press that blurred the
    // field reaches the submit button.
    useLayoutEffect(() => {
      input.current?.setCustomValidity(invalid ? WORDS.invalid : '')
    }, [invalid])

    return (
      <Field
        label={label}
        helper={helper}
        error={error ?? (invalid ? WORDS.invalid : undefined)}
        className={className}
      >
        {field => (
          <Popover
            open={open}
            onOpenChange={next => {
              setOpen(next)
              // Closed from inside the calendar the field never blurs again,
              // so what it holds is settled here.
              if (!next && document.activeElement !== input.current) settle()
            }}
            align="start"
            className="p-2"
            // The field keeps the focus: the calendar follows what is typed
            // and is reached with the down arrow.
            onOpenAutoFocus={event => event.preventDefault()}
            anchor={
              <>
                <input
                  ref={input}
                  {...field}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus={autoFocus}
                  disabled={disabled}
                  placeholder={quiet ? EMPTY_MARK : WORDS.placeholder}
                  value={text}
                  onFocus={() => setLeft(false)}
                  onBlur={event => {
                    // Into the calendar is still editing the field.
                    if (
                      !calendar.current?.contains(
                        event.relatedTarget as Node | null,
                      )
                    ) {
                      settle()
                    }
                  }}
                  onMouseDown={() => setOpen(true)}
                  onChange={event => type(event.target.value)}
                  onKeyDown={onKeyDown}
                  className={controlShell(quiet, 'pr-8 tabular-nums')}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  icon={CalendarDays}
                  disabled={disabled}
                  tabIndex={-1}
                  aria-label={WORDS.openCalendar}
                  // The focus stays in the field, which the calendar belongs to.
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => setOpen(!open)}
                  className={cn(
                    'absolute top-1/2 right-0.5 -translate-y-1/2 text-faint hover:text-foreground',
                    quiet && 'h-6 w-6',
                  )}
                />
              </>
            }
          >
            <Calendar
              rootRef={calendar}
              value={parsed ?? value ?? null}
              onPick={pick}
            />
          </Popover>
        )}
      </Field>
    )
  },
)

// ── The calendar ────────────────────────────────────────────────────────────

/**
 * A month of days, Monday first, six weeks high so the panel does not jump
 * between months. The title switches to the twelve months of a year, which
 * is how a day years back is reached in two presses. The arrows walk the
 * days, Page Up and Page Down the months, with Shift the years; Enter takes
 * the day the focus is on.
 */
function Calendar({
  rootRef,
  value,
  onPick,
}: {
  rootRef: RefObject<HTMLDivElement | null>
  value: string | null
  onPick: (iso: string | null) => void
}) {
  const selected = dayOf(value)
  const now = calendarDayOf(new Date())
  const [focused, setFocused] = useState<CalendarDay>(selected ?? now)
  const [view, setView] = useState<'days' | 'months'>('days')
  const [shownValue, setShownValue] = useState(value)
  const grid = useRef<HTMLDivElement>(null)

  // What is typed into the field moves the month shown to it.
  if (value !== shownValue) {
    setShownValue(value)
    if (selected) {
      setFocused(selected)
      setView('days')
    }
  }

  // A key that moves the day or the month moves the focus with it, also
  // into the next month — whose days render first, after the focused button
  // is gone.
  const keyed = useRef(false)
  useEffect(() => {
    if (!keyed.current) return
    keyed.current = false
    grid.current?.querySelector<HTMLButtonElement>('[tabindex="0"]')?.focus()
  }, [focused])

  const { year, month } = focused
  const days = useMemo(() => {
    const first = { year, month, day: 1 }
    const start = shift(first, -weekdayOf(first))
    return Array.from({ length: 42 }, (_, i) => {
      const day = shift(start, i)
      return {
        day,
        iso: isoOf(day),
        label: longDay.format(new Date(day.year, day.month - 1, day.day)),
      }
    })
  }, [year, month])

  const onGridKeyDown = (event: KeyboardEvent) => {
    const years = event.shiftKey ? 12 : 1
    const moves: Record<string, () => CalendarDay> =
      view === 'days'
        ? {
            ArrowLeft: () => shift(focused, -1),
            ArrowRight: () => shift(focused, 1),
            ArrowUp: () => shift(focused, -7),
            ArrowDown: () => shift(focused, 7),
            PageUp: () => shiftMonth(focused, -years),
            PageDown: () => shiftMonth(focused, years),
            Home: () => shift(focused, -weekdayOf(focused)),
            End: () => shift(focused, 6 - weekdayOf(focused)),
          }
        : {
            ArrowLeft: () => shiftMonth(focused, -1),
            ArrowRight: () => shiftMonth(focused, 1),
            ArrowUp: () => shiftMonth(focused, -3),
            ArrowDown: () => shiftMonth(focused, 3),
            PageUp: () => shiftMonth(focused, -12),
            PageDown: () => shiftMonth(focused, 12),
          }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    keyed.current = true
    setFocused(move())
  }

  const step = view === 'days' ? 1 : 12

  return (
    <div
      ref={rootRef}
      // A press in the calendar leaves the focus in the field: Safari gives a
      // pressed button no focus, so the field's blur could not tell it from
      // leaving.
      onMouseDown={event => event.preventDefault()}
      className="flex w-[15.5rem] flex-col gap-1.5"
    >
      <div className="flex flex-row items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          icon={ChevronLeft}
          aria-label={
            view === 'days' ? WORDS.previousMonth : WORDS.previousYear
          }
          onClick={() => setFocused(shiftMonth(focused, -step))}
        />
        <Button
          variant="ghost"
          size="sm"
          className="text-[13px] font-medium text-foreground"
          onClick={() => setView(view === 'days' ? 'months' : 'days')}
        >
          {view === 'days'
            ? monthTitle.format(new Date(year, month - 1, 1))
            : String(year)}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          icon={ChevronRight}
          aria-label={view === 'days' ? WORDS.nextMonth : WORDS.nextYear}
          onClick={() => setFocused(shiftMonth(focused, step))}
        />
      </div>

      <div
        ref={grid}
        role="grid"
        onKeyDown={onGridKeyDown}
        className={
          view === 'days'
            ? 'grid grid-cols-7 gap-0.5'
            : 'grid grid-cols-3 gap-1 py-1'
        }
      >
        {view === 'days' ? (
          <>
            {WORDS.weekdays.map(weekday => (
              <span
                key={weekday}
                className="flex h-7 items-center justify-center text-[11px] text-faint"
              >
                {weekday}
              </span>
            ))}
            {days.map(({ day, iso, label }) => (
              <CalendarCell
                key={iso}
                tabIndex={same(day, focused) ? 0 : -1}
                selected={same(day, selected)}
                current={same(day, now)}
                outside={day.month !== month}
                aria-label={label}
                onClick={() => onPick(iso)}
              >
                {day.day}
              </CalendarCell>
            ))}
          </>
        ) : (
          MONTH_NAMES.map((name, i) => (
            <CalendarCell
              key={name}
              tabIndex={i + 1 === month ? 0 : -1}
              className="h-10"
              selected={selected?.year === year && selected.month === i + 1}
              current={now.year === year && now.month === i + 1}
              onClick={() => {
                setFocused(shiftMonth(focused, i + 1 - month))
                setView('days')
              }}
            >
              {name}
            </CalendarCell>
          ))
        )}
      </div>

      <div className="flex flex-row items-center justify-between border-t border-line-soft pt-1.5">
        <Button variant="ghost" size="sm" onClick={() => onPick(todayIso())}>
          {COMMON.today}
        </Button>
        {value ? (
          <Button variant="ghost" size="sm" onClick={() => onPick(null)}>
            {WORDS.clear}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** One day or one month of the calendar: the picked one filled, today
 *  ringed, the days of the months either side faint. */
function CalendarCell({
  selected,
  current,
  outside,
  className,
  ...rest
}: {
  selected: boolean
  current: boolean
  outside?: boolean
} & Omit<ComponentPropsWithoutRef<'button'>, 'type'>) {
  return (
    <button
      type="button"
      aria-selected={selected}
      className={cn(
        'flex h-8 items-center justify-center rounded-md text-[13px] tabular-nums transition-colors',
        FOCUS_RING,
        selected
          ? 'bg-primary font-medium text-primary-foreground'
          : cn(
              'hover:bg-line-soft',
              outside ? 'text-faint' : 'text-foreground',
              current &&
                'font-semibold text-primary ring-1 ring-inset ring-primary/40',
            ),
        className,
      )}
      {...rest}
    />
  )
}
